import { useRef } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, Form, Input, InputNumber } from 'antd'
import type { BillSearchBatchInput } from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'
import { BillBatchResult } from './bill-batch-result.tsx'
import type { BillSearchBatchTaskInfo } from './bill-search-batch-task.tsx'
import { BILL_ACTION_ITEMS } from './const/bill-actions.ts'

export function BillSearchBatchDialog({
  action,
  search,
  count,
  onClose,
  onSaved,
  onQueued
}: {
  action: BillSearchBatchInput['action']
  search: BillSearchBatchInput['search']
  count: number
  onClose: () => void
  onSaved: () => Promise<void>
  onQueued: (task: BillSearchBatchTaskInfo) => void
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const key = useRef<string | undefined>(undefined)
  const save = useMutation(trpc.bill.batchBySearch.mutationOptions())
  const title = BILL_ACTION_ITEMS.find((item) => item.key === action)!.label
  const results = save.data?.results
  return (
    <ModalForm<{ amount?: string; reason?: string; note?: string }>
      open
      title={`按搜索${title}（${count} 张）`}
      width="40rem"
      layout="vertical"
      modalProps={{ onCancel: onClose, mask: { closable: false }, closable: !save.isPending }}
      onValuesChange={() => {
        key.current = undefined
      }}
      submitter={
        save.data
          ? false
          : {
              searchConfig: { submitText: '确认提交后台任务', resetText: '取消' },
              submitButtonProps: { loading: save.isPending, danger: action === 'delete' },
              resetButtonProps: { disabled: save.isPending, onClick: onClose }
            }
      }
      onFinish={async (values) => {
        try {
          if (key.current === undefined) key.current = crypto.randomUUID()
          const result = await save.mutateAsync({
            ...values,
            action,
            search,
            expectedCount: count,
            idempotencyKey: key.current
          })
          if (result.queued && result.jobId) {
            onQueued({ jobId: result.jobId, count: result.count, action })
            void message.success('已提交后台任务')
          } else {
            await onSaved()
          }
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '提交失败')
        }
        return false
      }}
    >
      <div className="flex flex-col gap-4">
        <Alert
          showIcon
          type="warning"
          title={`将处理全部 ${count} 张匹配账单，包含其他分页。超过 200 张由后台执行。`}
          description="各账单独立处理，失败不影响其他账单；任务提交后关闭弹窗不会取消执行。"
        />
        {!save.data && (
          <>
            {action === 'issue' && (
              <Alert
                type="info"
                title="按账单已保存金额出账；缺少金额或状态不符的账单将返回失败。需要逐张核定金额时，请缩小搜索范围。"
              />
            )}
            {action === 'amount' && (
              <>
                <Form.Item
                  name="amount"
                  label="每张账单统一修改为"
                  rules={[{ required: true, message: '请填写目标金额' }]}
                >
                  <InputNumber stringMode min="0" precision={2} disabled={save.isPending} />
                </Form.Item>
                <Form.Item name="reason" label="修改原因" rules={[{ required: true, whitespace: true }]}>
                  <Input.TextArea maxLength={1000} disabled={save.isPending} />
                </Form.Item>
              </>
            )}
            {action === 'note' && (
              <Form.Item name="note" label="新增备注" rules={[{ required: true, whitespace: true }]}>
                <Input.TextArea maxLength={5000} showCount disabled={save.isPending} />
              </Form.Item>
            )}
          </>
        )}
        {results && results.length > 0 && <BillBatchResult results={results} />}
        {save.data && <Button onClick={onClose}>关闭</Button>}
      </div>
    </ModalForm>
  )
}
